import React, { useMemo } from 'react';
import {
  Activity,
  CalendarClock,
  Hash,
  MessageCircle,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react';
import type { ForumBase } from '../types/forum';
import {
  aggregateCategoryForums,
  buildCategorySeoParagraph,
  formatCategoryLastUpdate,
  latestForumUpdateIso,
} from '../utils/categoryAggregates';
import { formatChangePercent, statDelta } from '../utils/forumStatChange';

interface Props {
  categoryName: string;
  categoryDescription?: string | null;
  forums: ForumBase[];
  darkMode: boolean;
}

const CategorySummaryPanel: React.FC<Props> = ({
  categoryName,
  categoryDescription,
  forums,
  darkMode,
}) => {
  const agg = useMemo(() => aggregateCategoryForums(forums), [forums]);
  const lastUpdateIso = useMemo(() => latestForumUpdateIso(forums), [forums]);
  const seoParagraph = useMemo(
    () => buildCategorySeoParagraph(categoryName, agg, lastUpdateIso, categoryDescription),
    [categoryName, agg, lastUpdateIso, categoryDescription]
  );

  const card = darkMode
    ? 'bg-gray-800/80 border border-gray-700'
    : 'bg-white shadow-sm border border-indigo-100';

  const statRow = (label: string, current: number, previous: number, icon: React.ReactNode) => {
    const delta = statDelta(current, previous);
    const pct = formatChangePercent(current, previous);
    return (
      <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-200/60 dark:border-gray-700/80 last:border-0">
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{label}</span>
        </div>
        <div className="text-right shrink-0">
          <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {current.toLocaleString('tr-TR')}
          </span>
          {delta !== null && pct && (
            <span
              className={`ml-2 text-xs inline-flex items-center gap-0.5 ${
                delta > 0 ? 'text-green-500' : delta < 0 ? 'text-red-500' : 'text-gray-400'
              }`}
            >
              {delta > 0 ? <ArrowUpRight size={12} /> : delta < 0 ? <ArrowDownRight size={12} /> : <Minus size={12} />}
              {pct}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <section
      className={`rounded-xl p-4 sm:p-6 h-full ${card}`}
      aria-labelledby="category-summary-heading"
    >
      <h2
        id="category-summary-heading"
        className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-indigo-900'}`}
      >
        Kategori özeti
      </h2>
      <p className={`text-base font-medium mb-2 break-words ${darkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>
        {categoryName}
      </p>
      <p className={`text-sm mb-4 flex items-center gap-2 flex-wrap ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <CalendarClock size={16} className="shrink-0" />
        Son güncelleme: {formatCategoryLastUpdate(lastUpdateIso)}
      </p>

      <div className="mb-4">
        <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-200/60 dark:border-gray-700/80">
          <div className="flex items-center gap-2">
            <Hash className="text-purple-500 shrink-0" size={18} />
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Forum sayısı</span>
          </div>
          <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {agg.forumCount.toLocaleString('tr-TR')}
          </span>
        </div>
        {statRow(
          'Toplam üye',
          agg.totalMembers,
          agg.previousTotalMembers,
          <Users className="text-blue-500 shrink-0" size={18} />
        )}
        {statRow(
          'Toplam konu',
          agg.totalTopics,
          agg.previousTotalTopics,
          <MessageCircle className="text-purple-500 shrink-0" size={18} />
        )}
        {statRow(
          'Toplam ileti',
          agg.totalPosts,
          agg.previousTotalPosts,
          <Activity className="text-green-500 shrink-0" size={18} />
        )}
      </div>

      <p
        className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
      >
        {seoParagraph}
      </p>
    </section>
  );
};

export default CategorySummaryPanel;
